import { Question } from "../types/question";
import { z } from "zod/v4";

export const createDynamicSchema = (questions: Question[]) => {
  const schemaMap: { [key: string]: any } = {};

  questions.forEach((question) => {
    // Handle different field types and their validations
    switch (question.type) {
      case "email": {
        schemaMap[question.name] = question.required
          ? z
              .string()
              .min(1, `${question.label} is required`)
              .email(`${question.label} must be a valid email address`)
          : z.string().optional().nullable();
        break;
      }

      case "tel": {
        let telSchema = z.string().optional().nullable();
        if (question.required) {
          schemaMap[question.name] = z
            .string()
            .regex(
              /^\+?[0-9]{1,4} ?[0-9]\d{6,14}$/,
              "Invalid phone number format"
            )
            .min(1, `${question.label} is required`);
        }
        schemaMap[question.name] = telSchema;
        break;
      }

      case "number": {
        if (question.required) {
          schemaMap[question.name] = z.number().min(-Infinity);
        } else {
          // For optional number fields
          schemaMap[question.name] = z.number().optional().nullable();
        }
        break;
      }

      case "dropdown": {
        if (question.options) {
          const validOptions = question.options.map((opt) => opt.key);
          if (question.required) {
            schemaMap[question.name] = z.enum(
              validOptions as [string, ...string[]]
            );
          } else {
            schemaMap[question.name] = z
              .enum(validOptions as [string, ...string[]])
              .optional()
              .nullable();
          }
        } else {
          schemaMap[question.name] = question.required
            ? z.string().min(1, `${question.label} is required`)
            : z.string().optional().nullable();
        }
        break;
      }

      case "checkbox": {
        let checkboxSchema: z.ZodTypeAny;

        if (question.required) {
          checkboxSchema = z
            .array(z.string())
            .min(1, `Please select at least one ${question.label}`);
        } else {
          checkboxSchema = z.array(z.string()).optional().nullable();
        }

        schemaMap[question.name] = checkboxSchema;
        break;
      }

      case "file": {
        if (question.required) {
          schemaMap[question.name] = z.string().url();
        } else {
          schemaMap[question.name] = z.string().url().optional().nullable();
        }
        break;
      }

      default: {
        if (question.required) {
          let textSchema = z.string();
          if (question.pattern) {
            textSchema = textSchema.regex(
              new RegExp(question.pattern),
              `${question.label} format is invalid`
            );
          }
          schemaMap[question.name] = textSchema.min(
            1,
            `${question.label} is required`
          );
        } else {
          schemaMap[question.name] = z.string().optional().nullable();
        }
        break;
      }
    }
  });

  return z.object(schemaMap);
};
